import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button, ConfigProvider, Drawer, Dropdown, Input, Tooltip, theme } from "antd";
import { ShieldSlash } from "@phosphor-icons/react";
import { PlatformAdminIcon, PlatformIconButton } from "../../features/platform-admin/PlatformAdminIcon.tsx";
import { platformTheme } from "../../features/platform-admin/platformAdminTheme.ts";
import { PortalShellPreferencesProvider } from "./PortalShellPreferences.tsx";
import "../../features/platform-admin/platform-admin-tokens.css";
import "./PortalShell.css";
import "./PortalAccountMenu.css";
import useMobileGrid from "../../components/shared/grid/useMobileGrid.ts";
import RajBrandFooter from "../../features/raj-brand/RajBrandFooter.tsx";

function readPreference(key: string, fallback: string) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

function writePreference(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* Storage is optional. */ }
}

export interface PortalNavigationItem<Key extends string> {
  key: Key;
  title: string;
  shortTitle?: string;
  icon: ReactNode;
  disabled?: boolean;
}

export interface PortalNavigationGroup<Key extends string> {
  id: string;
  title: string;
  shortTitle: string;
  icon: ReactNode;
  itemKeys: readonly Key[];
}

export interface PortalShellProps<Key extends string> {
  items: PortalNavigationItem<Key>[];
  groups?: readonly PortalNavigationGroup<Key>[];
  current: Key;
  accountKey?: Key;
  home: Key;
  onNavigate: (key: Key) => void;
  title: string;
  subtitle: string;
  userName: string;
  preferencePrefix: string;
  direction?: "rtl" | "ltr";
  language?: string;
  className?: string;
  translate?: (source: string, values?: Record<string, string>) => string;
  navigationStart?: (iconOnly: boolean) => ReactNode;
  headerActions?: ReactNode;
  mobileNavigation?: ReactNode;
  headerIdentity?: { title: string; subtitle: string };
  brand?: ReactNode;
  onMobileBrandClick?: () => void;
  navigationMode?: "grouped" | "expandable";
  accountControl?: "menu" | "logout" | "drawer";
  accountOpen?: boolean;
  onAccountOpen?: () => void;
  loggingOut: boolean;
  onLogout: () => void;
  children: ReactNode;
}

function persianLabel(source: string, values?: Record<string, string>) {
  return source.replace("{{title}}", values?.title || "");
}

// Sessions and authorized destinations belong to route adapters, not this shell.
export default function PortalShell<Key extends string>({
  items, groups, current, home, onNavigate, title, subtitle, userName,
  preferencePrefix, direction = "rtl", language = "fa", className = "",
  translate = persianLabel, navigationStart, headerActions, loggingOut, onLogout, children,
  brand, navigationMode = "grouped", accountControl = "menu", accountKey, accountOpen,
  onAccountOpen, headerIdentity, onMobileBrandClick, mobileNavigation,
}: PortalShellProps<Key>) {
  const expandableNavigation = navigationMode === "expandable";
  const mobile = useMobileGrid();
  const navigationId = useId();
  const [dark, setDark] = useState(function () { return readPreference(`${preferencePrefix}-theme`, "light") === "dark"; });
  const [collapsed, setCollapsed] = useState(function () {
    return expandableNavigation || readPreference(`${preferencePrefix}-sidebar`, "expanded") === "collapsed";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(function () {
    return groups?.find(function (group) { return group.itemKeys.includes(current); })?.id || groups?.[0]?.id;
  });
  const root = useRef<HTMLDivElement>(null);
  const palette = platformTheme[dark ? "dark" : "light"];
  const accountItem = items.find(function (item) { return item.key === accountKey; });

  useEffect(function () {
    setMobileOpen(false);
    setSelectedGroupId(groups?.find(function (group) { return group.itemKeys.includes(current); })?.id || groups?.[0]?.id);
  }, [current, groups]);

  useEffect(function () {
    document.title = `${items.find(function (item) { return item.key === current; })?.title || title} | رَج`;
  }, [current, items, title]);

  function openPage(next: Key) {
    if (groups) {
      setSelectedGroupId(groups.find(function (group) { return group.itemKeys.includes(next); })?.id);
    }
    onNavigate(next);
    setMobileOpen(false);
    setSearch("");
    setMenuSearch("");
  }

  function changeTheme(nextDark: boolean) {
    writePreference(`${preferencePrefix}-theme`, nextDark ? "dark" : "light");
    setDark(nextDark);
  }

  function filterNavigationItems(visibleItems: PortalNavigationItem<Key>[], query: string) {
    const normalizedQuery = query.trim().replace(/ي/g, "ی").replace(/ك/g, "ک").toLocaleLowerCase();
    return visibleItems.filter(function (item) {
      return item.key !== accountKey && item.title.toLocaleLowerCase().replace(/ي/g, "ی").replace(/ك/g, "ک").includes(normalizedQuery);
    });
  }

  function renderNavigationItems(visibleItems = items, query = search || menuSearch, compact = false, showTooltips = true) {
    const matches = filterNavigationItems(visibleItems, query);
    return (
      <>
        {matches.map(function (item) {
          const navigationButton = (
            <button
              key={item.key}
              type="button"
              className={`platform-admin__nav-item${item.key === current ? " is-active" : ""}`}
              aria-current={item.key === current ? "page" : undefined}
              aria-disabled={item.disabled || undefined}
              onClick={function () { if (!item.disabled) openPage(item.key); }}
            >
              {item.icon}
              <span>{compact ? item.shortTitle || item.title : item.title}</span>
              {item.disabled ? (
                <span
                  aria-label={translate("دسترسی داده نشده")}
                  className="portal-shell__permission-marker-trigger"
                  role="img"
                >
                  <ShieldSlash
                    className="portal-shell__permission-marker"
                    size={14}
                  />
                </span>
              ) : null}
            </button>
          );

          return showTooltips ? (
            <Tooltip
              key={item.key}
              title={item.disabled ? translate("دسترسی به این بخش فقط برای مدیر نمایندگی مجاز است.") : compact && item.shortTitle ? item.title : undefined}
              placement={direction === "rtl" ? "left" : "right"}
              trigger={["hover", "focus"]}
            >
              {navigationButton}
            </Tooltip>
          ) : navigationButton;
        })}
        {!matches.length ? (
          <p className="platform-admin__empty">
            {translate("گزینه‌ای با این عبارت پیدا نشد.")}
          </p>
        ) : null}
      </>
    );
  }

  function renderGroupedNavigation(mobile: boolean) {
    const visibleGroups = mobile || search ? groups : groups?.filter(function (group) { return group.id === selectedGroupId; });
    const visibleItems = visibleGroups?.flatMap(function (group) {
      return group.itemKeys.flatMap(function (key) {
        const item = items.find(function (candidate) { return candidate.key === key; });
        return item ? [item] : [];
      });
    }) || [];
    return renderNavigationItems(visibleItems, mobile ? "" : search || menuSearch);
  }

  function renderNavigation(mobile = false) {
    return (
      <>
        {!mobile ? (
        <div className="context-navigation__header">
          <Input
            aria-label={translate("جست‌وجو در منو")}
            prefix={<PlatformAdminIcon
              name="search"
              size={16}
            />}
            placeholder={translate("جست‌وجو در منو…")}
            value={menuSearch}
            onChange={function (event) {
              setMenuSearch(event.target.value);
              if (event.target.value) setSearch("");
            }}
          />
        </div>
        ) : null}
        <nav
          className="context-navigation__body"
          aria-label={translate("منوهای پورتال")}
        >
          {navigationStart?.(false)}
          {groups && !expandableNavigation ? renderGroupedNavigation(mobile) : renderNavigationItems(items, mobile ? "" : search || menuSearch)}
        </nav>
      </>
    );
  }

  function renderHeaderIdentity() {
    return (
      <div className="company-identity">
        <span className="company-identity__copy">
          <strong title={headerIdentity?.title || title}>
            {headerIdentity?.title || title}
          </strong>
          <small title={headerIdentity?.subtitle || subtitle}>
            {headerIdentity?.subtitle || subtitle}
          </small>
        </span>
      </div>
    );
  }

  function renderUtilities(compact = false) {
    const themeLabel = dark ? translate("حالت روشن") : translate("حالت تیره");
    const profileLabel = accountControl === "drawer"
      ? translate("حساب من")
      : translate("پورتال من");
    return (
      <>
        <Tooltip
          title={compact ? themeLabel : undefined}
          trigger={["hover", "focus"]}
        >
          <Button
            type="text"
            className="portal-shell__sidebar-action"
            icon={<PlatformAdminIcon name={dark ? "sun" : "moon"} />}
            onClick={function () {
              changeTheme(!dark);
            }}
          >
            <span>
              {translate("پوسته")}
            </span>
          </Button>
        </Tooltip>
        {accountControl === "logout" ? (
          <Button
            type="text"
            className="portal-shell__sidebar-action"
            icon={<PlatformAdminIcon name="logout" />}
            disabled={loggingOut}
            loading={loggingOut}
            onClick={onLogout}
          >
            <span>{loggingOut ? translate("در حال خروج…") : translate("خروج")}</span>
          </Button>
        ) : accountControl === "drawer" ? (
          <>
            <Button
              type="text"
              className={`portal-shell__sidebar-action${accountOpen ? " is-active" : ""}`}
              icon={accountItem?.icon || <PlatformAdminIcon name="profile" />}
              aria-expanded={accountOpen}
              disabled={loggingOut}
              onClick={function () {
                setMobileOpen(false);
                onAccountOpen?.();
              }}
            >
              <span>{profileLabel}</span>
            </Button>
            <Button
              type="text"
              className="portal-shell__sidebar-action"
              icon={<PlatformAdminIcon name="logout" />}
              disabled={loggingOut}
              loading={loggingOut}
              onClick={onLogout}
            >
              <span>{loggingOut ? translate("در حال خروج…") : translate("خروج")}</span>
            </Button>
          </>
        ) : (
          <Dropdown
            trigger={["click"]}
            placement="leftBottom"
            overlayClassName="portal-shell__account-dropdown portal-account-menu"
            menu={{
              items: [
                { key: "identity", label: userName, disabled: true },
                ...(accountItem ? [{
                  key: "profile",
                  label: accountItem.title,
                  icon: accountItem.icon,
                  disabled: loggingOut,
                  onClick: function () { openPage(accountItem.key); },
                }] : []),
                {
                  key: "logout",
                  label: loggingOut ? translate("در حال خروج…") : translate("خروج"),
                  disabled: loggingOut,
                  icon: <PlatformAdminIcon name="logout" />,
                  onClick: onLogout,
                },
              ],
            }}
          >
            <Button
              type="text"
              className={`portal-shell__sidebar-action${accountItem?.key === current ? " is-active" : ""}`}
              icon={accountItem?.icon || <PlatformAdminIcon name="profile" />}
              aria-current={accountItem?.key === current ? "page" : undefined}
              disabled={loggingOut}
              loading={loggingOut}
            >
              <span>
                {profileLabel}
              </span>
            </Button>
          </Dropdown>
        )}
      </>
    );
  }

  return (
    <div
      ref={root}
      className={`platform-admin ${dark ? "theme-dark" : "theme-light"}${collapsed ? " is-collapsed" : ""}${expandableNavigation ? " portal-shell--expandable-navigation" : ""}${mobileNavigation ? " portal-shell--shop-navigation" : ""} ${className}`}
      dir={direction}
      lang={language}
    >
      <ConfigProvider
        direction={direction}
        getPopupContainer={function () { return root.current || document.body; }}
        theme={{
          algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: {
            colorPrimary: palette.colorBrand,
            colorBgBase: palette.colorSurface,
            colorBgContainer: palette.colorSurface,
            colorBgElevated: palette.colorSurfaceRaised,
            colorText: palette.colorText,
            colorTextSecondary: palette.colorTextMuted,
            colorBorder: palette.colorBorder,
            colorSplit: palette.colorBorder,
            colorError: palette.colorDanger,
            fontFamily: "var(--portal-ui-font)",
            fontSize: 14,
            borderRadius: 6,
            controlHeight: 38,
          },
        }}
      >
        <PortalShellPreferencesProvider
          value={{ dark, setDark: changeTheme }}
        >
        <header className="global-header">
          <button
            className="raj-brand"
            type="button"
            onClick={function () {
              if (mobile && onMobileBrandClick) onMobileBrandClick();
              else openPage(home);
            }}
          >
            {brand ?? <span className="raj-brand__wordmark">رَج</span>}
          </button>
          {renderHeaderIdentity()}
          <Input
            className="global-search"
            aria-label={translate("جست‌وجو در همه منوها")}
            prefix={<PlatformAdminIcon
              name="search"
              size={18}
            />}
            placeholder={translate("جست‌وجو در همه منوها…")}
            value={search}
            onChange={function (event) {
              setSearch(event.target.value);
              if (event.target.value) setMenuSearch("");
              if (!expandableNavigation && event.target.value) setCollapsed(false);
            }}
          />
          {headerActions ? (
            <div className="portal-shell__header-actions">
              {headerActions}
            </div>
          ) : null}
          <PlatformIconButton
            className="platform-admin__mobile-menu"
            label={translate("بازکردن منوها")}
            icon="menu"
            onClick={function () { setMobileOpen(true); }}
          />
        </header>
        <div className="app-shell__body">
          <aside
            className="desktop-navigation"
            aria-label={translate("ناوبری اصلی")}
            onKeyDown={function (event) {
              if (expandableNavigation && event.key === "Escape") {
                setCollapsed(true);
                setSearch("");
                setMenuSearch("");
              }
            }}
          >
            <nav
              className={`module-rail${!expandableNavigation || collapsed ? " module-rail--compact" : ""}`}
              aria-label={translate(expandableNavigation ? "منوهای پورتال" : "حوزه‌های سامانه")}
            >
              <div
                id={navigationId}
                className="module-rail__items"
              >
                {navigationStart?.(false)}
                {expandableNavigation ? renderNavigationItems(items, search || menuSearch, collapsed, false) : groups?.map(function (group) {
                  return (
                    <button
                      key={group.id}
                      type="button"
                      className={group.id === selectedGroupId ? "is-active" : ""}
                      aria-label={group.title}
                      aria-pressed={group.id === selectedGroupId}
                      onClick={function () {
                        setSelectedGroupId(group.id);
                        setCollapsed(false);
                        setSearch("");
                        setMenuSearch("");
                      }}
                    >
                      {group.icon}
                      <span>{group.shortTitle}</span>
                    </button>
                  );
                })}
              </div>
              <div
                className="portal-shell__sidebar-utilities"
                role="group"
                aria-label={translate("ابزارهای سامانه")}
              >
                {renderUtilities(!expandableNavigation || collapsed)}
              </div>
              <footer className="module-rail__footer">
                <PlatformIconButton
                  label={collapsed ? translate("بازکردن منوها") : translate("بستن منوها")}
                  icon={collapsed ? "expand" : "collapse"}
                  aria-expanded={!collapsed}
                  aria-controls={navigationId}
                  onClick={function () {
                    setCollapsed(!collapsed);
                    if (!expandableNavigation) writePreference(`${preferencePrefix}-sidebar`, collapsed ? "expanded" : "collapsed");
                  }}
                />
              </footer>
            </nav>
            {!expandableNavigation && !collapsed ? (
              <section className="context-navigation">
                {renderNavigation()}
              </section>
            ) : null}
          </aside>
          <div className="app-content-frame">
            {children}
            <RajBrandFooter />
          </div>
        </div>
        {mobileNavigation ? (
          <div
            className="portal-shell__mobile-navigation-slot"
            hidden={mobileOpen}
          >
            {mobileNavigation}
          </div>
        ) : null}
        <Drawer
          title={title}
          className="portal-shell__mobile-navigation"
          placement="right"
          open={mobileOpen}
          onClose={function () { setMobileOpen(false); }}
          getContainer={false}
          width="min(22rem, calc(100vw - 2rem))"
          footer={(
            <div
              className="portal-shell__sidebar-utilities"
              role="group"
              aria-label={translate("ابزارهای سامانه")}
            >
              {renderUtilities()}
            </div>
          )}
        >
          {renderNavigation(true)}
        </Drawer>
        </PortalShellPreferencesProvider>
      </ConfigProvider>
    </div>
  );
}
